import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Check,
  CircleAlert,
  Download,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { DocumentUpload, ResolvedDocument, ResolvedDocumentPair } from "../dtos";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import InlineAppeal from "./appeal-thread-inline";
import {NoteAttachmentList} from "./note-attachments";
import { secondaryButtonSx } from "./field-shell";
import { displayFileName } from "../utils/file-name";

type DocSide = "front" | "back";

/** Small status chip matching the module's pill styling (see DocumentField's Required chip). */
const Chip = ({ label, color, bg, icon }: { label: string; color: string; bg: string; icon?: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.4,
      fontSize: "10px",
      fontWeight: 700,
      lineHeight: 1,
      px: 0.75,
      py: 0.4,
      borderRadius: "999px",
      color,
      backgroundColor: bg,
      whiteSpace: "nowrap",
    }}
  >
    {icon}
    {label}
  </Box>
);

/** §Manual-AML: the pair's screening outcome as a chip tone (same ramp as DocumentField). */
const amlChip = (aml: NonNullable<DocumentUpload["aml"]>) => {
  const status = (aml.status ?? "").toUpperCase();
  if (aml.stale) return { label: "AML re-check needed", color: "#92400e", bg: "#fef3c7" };
  if (status === "CLEAR" || status === "NO_MATCH")
    return { label: "AML clear", color: "#166534", bg: "#dcfce7" };
  if (/MATCH|HIT/.test(status)) return { label: "AML match", color: "#b91c1c", bg: "#fee2e2" };
  return { label: `AML ${status.toLowerCase()}`, color: "#6b7280", bg: "#f3f4f6" };
};

/** One Front/Back slot: the file when filled, an upload affordance when empty. */
const SideSlot = ({
  side,
  upload,
  disabled,
  busy,
  onUpload,
  onDownload,
  onDelete,
}: {
  side: DocSide;
  upload: DocumentUpload | null;
  disabled: boolean;
  busy: boolean;
  onUpload: (file: File) => void;
  onDownload: (uploadUid: string, fileName: string) => void;
  onDelete: (uploadUid: string) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const inputRef = useRef<HTMLInputElement>(null);
  const title = side === "front" ? "Front" : "Back";
  // The back is where the dates live — saying so is what stops an agent uploading the same face twice.
  const hint = side === "front" ? "Name, address, ID number" : "Issuance & expiry dates";
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 180,
        px: 1.25,
        py: 1,
        border: `1px ${upload ? "solid" : "dashed"} ${colors.semantic.border.secondary}`,
        borderRadius: theme.nawy.radius.md,
        backgroundColor: upload ? colors.semantic.layer.primary : "transparent",
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography variant="caption" fontWeight={600}>
          {title}
        </Typography>
        {!upload && (
          <Typography variant="caption" color={colors.semantic.text.secondary}>
            — {hint}
          </Typography>
        )}
      </Stack>
      {upload ? (
        <>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          <FileText size={14} color={colors.primitives.blue[500]} />
          <Typography variant="caption" sx={{ flex: 1, wordBreak: "break-all" }}>
            {displayFileName(upload.fileName)}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onDownload(upload.uid, displayFileName(upload.fileName))}
            aria-label={`Download ${title.toLowerCase()}`}
          >
            <Download size={14} />
          </IconButton>
          {!disabled && (
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              aria-label={`Replace ${title.toLowerCase()}`}
            >
              <RefreshCw size={13} />
            </IconButton>
          )}
          {!disabled && (
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => onDelete(upload.uid)}
              aria-label={`Delete ${title.toLowerCase()}`}
            >
              <Trash2 size={14} />
            </IconButton>
          )}
        </Stack>
        {upload.rejectionReason && (
          <Typography variant="caption" sx={{display: "block", mt: 0.5, color: colors.semantic.text.extension.negative}}>
            {upload.rejectionReason}
          </Typography>
        )}
        <NoteAttachmentList attachments={upload.decisionAttachments} />
        {upload.resubmitted?.previousReason && (
          <Typography variant="caption" sx={{display: "block", mt: 0.5, color: colors.semantic.text.secondary}}>
            Previous decision: {upload.resubmitted.previousReason}
          </Typography>
        )}
        <NoteAttachmentList attachments={upload.resubmitted?.previousAttachments} />
        </>
      ) : (
        !disabled && (
          <Button
            size="small"
            startIcon={busy ? <CircularProgress size={13} /> : <Upload size={13} />}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            sx={{ ...secondaryButtonSx(theme), mt: 0.5 }}
          >
            Upload {title.toLowerCase()}
          </Button>
        )
      )}
      {!disabled && (
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
            event.target.value = "";
          }}
        />
      )}
    </Box>
  );
};

/** One owner: two slots, the pair's combined extracted values (inline-editable), AML + Confirm. */
const OwnerPairCard = ({
  pair,
  index,
  total,
  fieldKey,
  disabled,
  amlEnabled,
  mutations,
}: {
  pair: ResolvedDocumentPair;
  index: number;
  total: number;
  fieldKey: string;
  disabled: boolean;
  amlEnabled: boolean;
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const anchor = pair.front ?? pair.back;
  const aml = pair.front?.aml ?? pair.back?.aml;
  const uploadingHere =
    mutations.upload.isLoading &&
    mutations.upload.variables?.fieldKey === fieldKey &&
    mutations.upload.variables?.pairSlot?.pairUid === pair.pairUid;
  const confirming =
    mutations.confirmPair.isLoading && mutations.confirmPair.variables?.pairUid === pair.pairUid;
  const screening =
    mutations.amlScreen.isLoading && mutations.amlScreen.variables?.uploadUid === anchor?.uid;
  const busy = uploadingHere || confirming || mutations.removeUpload.isLoading;

  const saveEdit = (ocrFieldKey: string) => {
    mutations.editExtraction.mutate({
      docKey: fieldKey,
      ocrFieldKey,
      value: editValue,
      // §Two-sided: the correction targets THIS owner's pair (required with several owners)
      pairUid: pair.pairUid,
    });
    setEditingKey(null);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.semantic.border.secondary}`,
        borderRadius: theme.nawy.radius.lg,
        p: { mobile: 1, tablet: 1.25 },
        mt: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={700}>
          {total > 1 ? `Owner ${index + 1}` : "Owner"}
        </Typography>
        {pair.verified ? (
          <Chip label="Confirmed" color="#166534" bg="#dcfce7" icon={<Check size={11} />} />
        ) : (
          pair.complete && <Chip label="Needs confirmation" color="#92400e" bg="#fef3c7" />
        )}
        {!pair.complete && (
          <Chip
            label={`${pair.front ? "Back" : "Front"} missing`}
            color="#92400e"
            bg="#fef3c7"
            icon={<CircleAlert size={11} />}
          />
        )}
        {amlEnabled && aml && (
          <Chip
            {...amlChip(aml)}
            icon={
              /MATCH|HIT/.test((aml.status ?? "").toUpperCase()) ? (
                <ShieldAlert size={11} />
              ) : (
                <ShieldCheck size={11} />
              )
            }
          />
        )}
      </Stack>

      {/* §Layout (product ask, 2026-08-06): side-by-side slots on desktop — the old xs/sm keys
          don't exist in nawyTheme (mobile/tablet/desktop only), so MUI silently dropped the
          responsive direction and the slots always stacked full-width. */}
      <Stack
        direction={{ mobile: "column", tablet: "row" }}
        spacing={1}
        sx={{ mt: 1 }}
      >
        {(["front", "back"] as const).map((side) => (
          <SideSlot
            key={side}
            side={side}
            upload={pair[side]}
            disabled={disabled}
            busy={busy}
            onUpload={(file) =>
              mutations.upload.mutate({
                fieldKey,
                file,
                isGlobalDocument: true,
                pairSlot: { side, pairUid: pair.pairUid },
              })
            }
            onDownload={mutations.download}
            onDelete={(uploadUid) => mutations.removeUpload.mutate({ docKey: fieldKey, uploadUid })}
          />
        ))}
      </Stack>

      {uploadingHere && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={13} />
          <Typography variant="caption" color={colors.semantic.text.secondary}>
            Reading both sides…
          </Typography>
        </Stack>
      )}

      {/* The pair's OWN values — read from both images together. Empty while an extraction is
          pending or a side was just replaced, which is deliberate: the previous owner's values
          are never shown. */}
      {pair.complete && !uploadingHere && (
        <Box sx={{ mt: 1 }}>
          {/* §EmptyOCR (product ask, 2026-08-06): a scan the OCR couldn't read still renders every
              field — EMPTY and editable — so the agent types the values off the physical card and
              confirms, exactly like the WM. The dead-end "no values" note only remains for a
              document with no extraction fields configured at all. */}
          {pair.values.length ? (
            pair.values.map((row) => (
              <Stack
                key={row.key}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 0.25, minHeight: 28 }}
              >
                <Typography
                  variant="caption"
                  color={colors.semantic.text.secondary}
                  sx={{ minWidth: 140 }}
                >
                  {row.label}
                </Typography>
                {editingKey === row.key ? (
                  <>
                    <TextField
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: theme.nawy.radius.xl } }}
                      value={editValue}
                      onChange={(event) => setEditValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") saveEdit(row.key);
                        if (event.key === "Escape") setEditingKey(null);
                      }}
                      autoFocus
                      inputProps={{ dir: "auto", style: { fontSize: 13, padding: "4px 8px" } }}
                    />
                    <IconButton size="small" onClick={() => saveEdit(row.key)} aria-label="Save value">
                      <Check size={14} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditingKey(null)} aria-label="Cancel edit">
                      <X size={14} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography variant="caption" dir="auto" sx={{ fontWeight: 600 }}>
                      {row.value === null || row.value === undefined || row.value === ""
                        ? "—"
                        : String(row.value)}
                    </Typography>
                    {!disabled && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingKey(row.key);
                          setEditValue(row.value == null ? "" : String(row.value));
                        }}
                        aria-label={`Edit ${row.label}`}
                      >
                        <Pencil size={12} />
                      </IconButton>
                    )}
                  </>
                )}
              </Stack>
            ))
          ) : (
            <Typography variant="caption" color={colors.semantic.text.secondary}>
              <em>No values read from this card yet.</em>
            </Typography>
          )}
        </Box>
      )}

      {!disabled && pair.complete && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {!pair.verified && (
            <Button
              size="small"
              variant="contained"
              // §EmptyOCR: confirm stays offered even when the OCR read nothing — the agent
              // fills the rows by hand first; the ENGINE owns any refusal, with its own message
              disabled={busy}
              startIcon={confirming ? <CircularProgress size={13} /> : <Check size={13} />}
              onClick={() => mutations.confirmPair.mutate({ docKey: fieldKey, pairUid: pair.pairUid })}
              sx={secondaryButtonSx(theme)}
            >
              {confirming ? "Confirming…" : "Confirm this owner"}
            </Button>
          )}
          {/* §Manual-AML parity (WM 1cec8c6 + flat-card rule above): a CURRENT result retires the
              control — it re-offers itself only when a subject edit makes the screening stale.
              This card used to keep a standing "Re-run AML check" after a clear result. */}
          {amlEnabled && anchor && (!aml || aml.stale) && (
            <Button
              size="small"
              variant="outlined"
              disabled={busy || screening || !pair.actionable}
              startIcon={screening ? <CircularProgress size={13} /> : <ShieldCheck size={13} />}
              onClick={() => mutations.amlScreen.mutate({ docKey: fieldKey, uploadUid: anchor.uid })}
              sx={secondaryButtonSx(theme)}
            >
              {screening ? "Screening…" : aml?.stale ? "Re-run AML check" : "Run AML check"}
            </Button>
          )}
        </Stack>
      )}
      {/* §Review-appeal: one per filled side — this card shows no per-file decision of its own, so the
          appeal is the only place a returned face surfaces here. Each renders nothing unless that exact
          file carries a standing rejection or already has a thread. */}
      {([pair.front, pair.back].filter(Boolean) as DocumentUpload[]).map((file) => (
        <InlineAppeal
          key={file.uid}
          anchor={{ kind: "document", fieldKey, uploadUid: file.uid }}
          label={total > 1 ? `owner ${index + 1}` : undefined}
        />
      ))}
    </Box>
  );
};

/**
 * §Two-sided documents — one card per OWNER, each holding a Front and a Back slot (WM parity,
 * PR #229/#216). The unit is deliberately the PAIR, not the file: OCR reads both images in a
 * single pass (the front carries the name/address/ID, the back the issuance and expiry dates),
 * AML screens the resulting identity ONCE per person, and Confirm belongs to the owner —
 * confirming one owner must never authorise the check or step completion for an owner nobody
 * reviewed. There is deliberately no document-level "Extracted values" block: a two-sided
 * document has no document-level values at all, because one value slot cannot hold N owners.
 */
const TwoSidedDocument = ({
  fieldKey,
  document,
  disabled,
  mutations,
}: {
  fieldKey: string;
  document: ResolvedDocument;
  disabled: boolean;
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const addOwnerRef = useRef<HTMLInputElement>(null);

  const pairs = document.pairs ?? [];
  // §Two-sided: a file uploaded BEFORE this document became two-sided has no side/pair. It blocks
  // completion rather than counting toward it — assigning or deleting it is the way out.
  const legacy = (document.uploads ?? []).filter((file) => !file.superseded && !file.pairUid);
  const allowMultipleOwners = Boolean(document.multiple);
  const amlEnabled = Boolean(document.amlEnabled);
  const assigning = mutations.assignSide.isLoading;

  // A new owner's first upload starts their pair; the server allocates the pairUid.
  const newOwnerUpload =
    mutations.upload.isLoading &&
    mutations.upload.variables?.fieldKey === fieldKey &&
    mutations.upload.variables?.pairSlot?.side === "front" &&
    !mutations.upload.variables?.pairSlot?.pairUid;

  /**
   * Which owner should a legacy file join? Two files left over from before the flip are almost
   * always the two faces of ONE card, so filing the first as the front must leave the second able
   * to COMPLETE that owner rather than open a second, permanently half-empty one. Only unambiguous
   * when exactly one pair is waiting for that side; otherwise the file starts a new owner.
   */
  const openPairFor = (side: DocSide): string | undefined => {
    const waiting = pairs.filter((pair) => !pair[side]);
    return waiting.length === 1 ? waiting[0].pairUid : undefined;
  };

  return (
    <Box>
      {legacy.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            border: `1px solid ${colors.semantic.border.secondary}`,
            borderRadius: theme.nawy.radius.lg,
            backgroundColor: "#fffbeb",
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ color: "#92400e" }}>
            Uploaded before front/back was required
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#92400e" }}>
            Tell us which side each file is, or delete it — the step cannot be submitted while they
            are unassigned.
          </Typography>
          {legacy.map((upload) => (
            <Stack
              key={upload.uid}
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.75 }}
            >
              <FileText size={14} color={colors.primitives.blue[500]} />
              <Typography variant="caption" sx={{ flex: 1, wordBreak: "break-all", minWidth: 120 }}>
                {displayFileName(upload.fileName)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => mutations.download(upload.uid, displayFileName(upload.fileName))}
                aria-label={`Download ${displayFileName(upload.fileName)}`}
              >
                <Download size={14} />
              </IconButton>
              {!disabled && (
                <>
                  <Button
                    size="small"
                    disabled={assigning}
                    onClick={() =>
                      mutations.assignSide.mutate({
                        docKey: fieldKey,
                        uploadUid: upload.uid,
                        side: "front",
                        pairUid: openPairFor("front"),
                      })
                    }
                    sx={secondaryButtonSx(theme)}
                  >
                    Use as front
                  </Button>
                  <Button
                    size="small"
                    disabled={assigning}
                    onClick={() =>
                      mutations.assignSide.mutate({
                        docKey: fieldKey,
                        uploadUid: upload.uid,
                        side: "back",
                        pairUid: openPairFor("back"),
                      })
                    }
                    sx={secondaryButtonSx(theme)}
                  >
                    Use as back
                  </Button>
                  <IconButton
                    size="small"
                    disabled={mutations.removeUpload.isLoading}
                    onClick={() =>
                      mutations.removeUpload.mutate({ docKey: fieldKey, uploadUid: upload.uid })
                    }
                    aria-label={`Delete ${displayFileName(upload.fileName)}`}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </>
              )}
            </Stack>
          ))}
        </Box>
      )}

      {pairs.length === 0 && legacy.length === 0 && (
        <Typography
          variant="caption"
          color={colors.semantic.text.secondary}
          sx={{ display: "block", mt: 1 }}
        >
          <em>Front and back not uploaded</em>
        </Typography>
      )}

      {pairs.map((pair, index) => (
        <OwnerPairCard
          key={pair.pairUid}
          pair={pair}
          index={index}
          total={pairs.length}
          fieldKey={fieldKey}
          disabled={disabled}
          amlEnabled={amlEnabled}
          mutations={mutations}
        />
      ))}

      {!disabled && (pairs.length === 0 || allowMultipleOwners) && (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={newOwnerUpload ? <CircularProgress size={14} /> : <Plus size={14} />}
            disabled={newOwnerUpload}
            onClick={() => addOwnerRef.current?.click()}
            sx={{ ...secondaryButtonSx(theme), mt: 1.5 }}
          >
            {pairs.length === 0 ? "Upload front of the card" : "Add another owner"}
          </Button>
          <input
            ref={addOwnerRef}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // No pairUid ⇒ the server starts a new owner's pair. The FRONT leads — it carries
              // the identity the back merely dates.
              if (file)
                mutations.upload.mutate({
                  fieldKey,
                  file,
                  isGlobalDocument: true,
                  pairSlot: { side: "front" },
                });
              event.target.value = "";
            }}
          />
        </>
      )}
    </Box>
  );
};

export default TwoSidedDocument;
